/**
 * Central service definitions for DS|KYC application
 */

export interface ServiceItem {
  key: string
  label: string
  desc: string
}

export interface ServiceDetail extends ServiceItem {
  desc: string
  costKey?: string
}

export const SERVICE_DETAILS: ServiceDetail[] = [
  // { key: 'kycCheck', label: 'KYC Check', desc: 'KYC-Prüfung für Mandanten', costKey: 'schufaOne' },
  { key: 'amlSearch', label: 'AML Check', desc: 'KYC-Prüfung für Mandanten', costKey: 'amlSearch' },
  {
    key: 'amlMonitoring',
    label: 'AML Monitoring',
    desc: 'Überwachung von Unternehmen auf neue Einträge im Handelsregister',
    costKey: 'aml-monitoring',
  },

  { key: 'dsIdentity', label: 'DS Identity', desc: 'Videoidentifikation', costKey: 'identity' },
  {
    key: 'topograph',
    label: 'Unternehmensdaten',
    desc: 'Internationale Unternehmensdaten',
    costKey: 'topograph',
  },

  {
    key: 'handelsregister',
    label: 'Handelsregister Deutschland',
    desc: 'Abfrage Handelsregister',
    costKey: 'handelsregister',
  },
  {
    key: 'transparenzregister',
    label: 'Transparenzregister DE',
    desc: 'Abfrage Transparenzregister',
    costKey: 'transparenzregister',
  },

  {
    key: 'handelsregisterCh',
    label: 'Handelsregister Schweiz',
    desc: 'Abfrage Handelsregister Schweiz',
    costKey: 'handelsregister-ch',
  },
  {
    key: 'transparenzCh',
    label: 'Transparenzregister CH',
    desc: 'Abfrage Transparenzregister CH',
    costKey: 'transparenzregister-ch',
  },
  { key: 'uidSearch', label: 'UID-Prüfung CH', desc: 'Abfrage UID', costKey: 'uid' },

  { key: 'stammdaten', label: 'Stammdaten', desc: 'Abfrage Stammdaten', costKey: 'stammdaten' },

  {
    key: 'dsSign',
    label: 'DS Sign (SES)',
    desc: 'Einfache elektronische Signatur (pro Unterzeichner)',
    costKey: 'dsSignSes',
  },
  {
    key: 'dsSign',
    label: 'DS Sign (AES)',
    desc: 'Fortgeschrittene elektronische Signatur (pro Unterzeichner)',
    costKey: 'dsSignAes',
  },
  {
    key: 'dsSign',
    label: 'DS Sign (QES)',
    desc: 'Qualifizierte elektronische Signatur (pro Unterzeichner)',
    costKey: 'dsSignQes',
  },
  {
    key: 'dsSignQesFirst',
    label: 'DS Sign (QES)',
    desc: 'Qualifizierte elektronische Signatur - einmalige Kosten für Identifikation ggü. SwissCom',
    costKey: 'dsSignQesFirst',
  },
  {
    key: 'importAbacus',
    label: 'Import aus Abacus',
    desc: 'Mandantenimport aus Abacus',
    costKey: undefined,
  },
  {
    key: 'importBexio',
    label: 'Import aus Bexio',
    desc: 'Mandantenimport aus Bexio',
    costKey: undefined,
  },
  {
    key: 'importExcel',
    label: 'Import aus Excel',
    desc: 'Mandantenimport aus Excel-Datei',
    costKey: undefined,
  },
]

/**
 * Get all service definitions
 */
export function getServices(): ServiceItem[] {
  return SERVICE_DETAILS
}

/**
 * Get service labels as key-value object (useful for quick lookups)
 */
export function getServiceLabels(): Record<string, string> {
  return SERVICE_DETAILS.reduce(
    (acc, service) => {
      acc[service.key] = service.label
      if (service.costKey) acc[service.costKey] = service.label
      return acc
    },
    {} as Record<string, string>
  )
}

/**
 * Get service label by key. Usage logs store the costKey (e.g. 'identity', 'uid',
 * 'aml-monitoring') rather than the service's own key, so both are matched here.
 */
export function getServiceLabel(key: string): string {
  if (key === 'kycCheck') return 'KYC Check'

  const service = SERVICE_DETAILS.find(s => s.key === key || s.costKey === key)
  return service?.label || key
}

/**
 * Get all service keys
 */
export function getServiceKeys(): string[] {
  return SERVICE_DETAILS.map(s => s.key)
}

/**
 * Get services for billing/purchase display (relevant credit services)
 */
export function getBillingServices(): ServiceItem[] {
  return SERVICE_DETAILS.filter(s =>
    ['dsIdentity', 'dsSign', 'transparenzregister', 'aml'].includes(s.key)
  )
}

/**
 * Get detailed service information (including description and cost key)
 */
export function getServiceDetails(): ServiceDetail[] {
  return SERVICE_DETAILS
}

/**
 * Get services for usage tracking display
 */
export function getServiceUsageItems(): ServiceDetail[] {
  return SERVICE_DETAILS.filter(s =>
    ['dsIdentity', 'dsSign', 'transparenzregister', 'handelsregister', 'amlSearch'].includes(s.key)
  )
}
